import { Injectable } from "@angular/core";
import { Observable, catchError, retry } from "rxjs";
import { BaseService } from "./base.service";
import { HttpClient } from "@angular/common/http";
import { MatSnackBar } from "@angular/material/snack-bar";
import { TranslateService } from "@ngx-translate/core";
import { CookieService } from "ngx-cookie-service";
import { UserActivation } from "../models/userActivation";
import { UserRegistration } from "../models/userRegistration";
import { User } from "../models/user";
import { Tenant } from "../models/tenant";
import { Role } from "../models/role";

@Injectable({
    providedIn: 'root'
  })
  export class ManagementService extends BaseService {

    private API_MANAGEMENT_URL = this.API_BASE_URL + "management"; 

    constructor(private httpClient: HttpClient,
        public _snackBar: MatSnackBar,
        public cookieService: CookieService,
        public translate: TranslateService) {
            super()
      }

    checkUserForResetPassword(email: string) {
        let body = {
            username: email
        }
        return this.httpClient.post(this.API_MANAGEMENT_URL + "/user/reset-password", body, {observe: 'response'});
    }

    ////////////////////////////////////////////////////////////////////////////
    //////////////////                GET                     //////////////////
    ////////////////////////////////////////////////////////////////////////////

    getUserRequest(email: string): Observable<User> {
      // This is to ensure that if you use a URL with special characters, they will be properly URL encoded (example heinrich@idealsolution.co.za and heinrich+1@idealsolution.co.za will be passed through correctly)
      let urlSafeEmail = encodeURIComponent(email);
      return this.httpClient.get<User>(this.API_MANAGEMENT_URL + "/user/findByEmail?email=" + urlSafeEmail)
        .pipe(
          retry(1),
          catchError(err => {
            this.hasError.next(true);
            this._snackBar.open(this.translate.instant('DATA-SERVICE.USER'), this.translate.instant('SNACKBAR.DISMISS'), {
              duration: 5000,
            });
            this.hasError.next(false);
            return this.errorHandler(err);
          })
        );
    }

    getUserTenants(userId: number): Observable<Tenant[]> {
      return this.httpClient.get<Tenant[]>(this.API_MANAGEMENT_URL + "/tenant/findTenantByUserId?userId=" + userId)
        .pipe(
          retry(1),
          catchError(err => {
            this.hasError.next(true);
            this._snackBar.open(this.translate.instant('DATA-SERVICE.TENANT'), this.translate.instant('SNACKBAR.DISMISS'), {
              duration: 5000,
            });
            this.hasError.next(false);
            return this.errorHandler(err);
          })
        );
    }

    userHashedPassword(email: string): Observable<any> {
      return this.httpClient.get<any>(this.API_MANAGEMENT_URL + "/user/findUserPasswordHashed?email=" + email)
        .pipe(
          catchError(err => {
            this.hasError.next(true);
            this._snackBar.open(this.translate.instant('DATA-SERVICE.PASSWORD'), this.translate.instant('SNACKBAR.DISMISS'), {
              duration: 5000,
            });
            this.hasError.next(false);
            return this.errorHandler(err);
          })
        );
    }

    getUserRoles(userId: number): Observable<Role[]> {
      return this.httpClient.get<Role[]>(this.API_MANAGEMENT_URL + "/role/findRoleByUserId?userId=" + userId)
        .pipe(
          //catchError(this.errorHandler)
          catchError(err => {
            this.hasError.next(true);
            this._snackBar.open(this.translate.instant('DATA-SERVICE.USER-ROLES'), this.translate.instant('SNACKBAR.DISMISS'), {
              duration: 5000,
            });
            this.hasError.next(false);
            return this.errorHandler(err);
          })
        );
    }

    ////////////////////////////////////////////////////////////////////////////
    ////////////////////             INSERT                  ///////////////////
    ////////////////////////////////////////////////////////////////////////////

    registerUser(userRegistration: UserRegistration): Observable<any> {
      return this.httpClient.post<any>(this.API_MANAGEMENT_URL + "/user/register", userRegistration)
        .pipe(
          catchError(err => {
            this.hasError.next(true);
            this._snackBar.open(this.translate.instant('DATA-SERVICE.REGISTER-USER'), this.translate.instant('SNACKBAR.DISMISS'), {
              duration: 5000,
            });
            this.hasError.next(false);
            return this.errorHandler(err);
          })
        );
    }

    ////////////////////////////////////////////////////////////////////////////
    ////////////////////             UPDATE                  ///////////////////
    ////////////////////////////////////////////////////////////////////////////

    activateUser(userActivation: UserActivation): Observable<any> {
      return this.httpClient.patch<any>(this.API_MANAGEMENT_URL + "/user/activation", userActivation)
      .pipe(
        catchError(err => {
          this.hasError.next(true);
          this._snackBar.open(this.translate.instant('DATA-SERVICE.ACTIVATE-USER'), this.translate.instant('SNACKBAR.DISMISS'), {
            duration: 5000,
          });
          this.hasError.next(false);
          return this.errorHandler(err);
        })
      );
    }

    updateUser(user: User): Observable<any> {
      return this.httpClient.put<any>(this.API_MANAGEMENT_URL + "/user/updateUser", user)
        .pipe(
          catchError(err => {
            this.hasError.next(true);
            this._snackBar.open(this.translate.instant('DATA-SERVICE.UPDATE-USER'), this.translate.instant('SNACKBAR.DISMISS'), {
              duration: 5000,
            });
            this.hasError.next(false);
            return this.errorHandler(err);
          })
        );
    }

    updatePassword(user: User): Observable<any> {
      return this.httpClient.put<any>(this.API_MANAGEMENT_URL + "/user/updateUserPassword", user)
        .pipe(
          catchError(err => {
            this.hasError.next(true);
            this._snackBar.open(this.translate.instant('DATA-SERVICE.UPDATE-PASSWORD'), this.translate.instant('SNACKBAR.DISMISS'), {
              duration: 5000,
            });
            this.hasError.next(false);
            return this.errorHandler(err);
          })
        );
    }

    ////////////////////////////////////////////////////////////////////////////
    ////////////////////             DELETE                  ///////////////////
    ////////////////////////////////////////////////////////////////////////////
    
    deleteUserById(userId: number): Observable<any> {
      return this.httpClient.delete<any>(this.API_MANAGEMENT_URL + "/user/" + userId)
          .pipe(
              catchError(err => {
                  this.hasError.next(true);
                  this._snackBar.open(this.translate.instant('DATA-SERVICE.DELETE-USER'), this.translate.instant('SNACKBAR.DISMISS'), {
                      duration: 5000,
                  });
                  this.hasError.next(false);
                  return this.errorHandler(err);
              })
          );
    }

  }  
